import {
    DeliveryProcessingTimeUnit,
    DeliveryProfile,
    DeliveryRateType,
    SetDeliveryProfileInput,
} from '@shopify-multiseller/graphql/partner/types';
import { getCountryName } from '../../helpers/country';

export interface OptionItem {
    label: string;
    value: string;
}

export interface ProcessingTimePreset {
    label: string;
    value: string;
    min?: number;
    max?: number;
}

export interface DeliveryProfileForm {
    name: string;
    saveProfile: boolean;
    originCountryCode: string;
    originRegionCode?: string;
    originPostalCode?: string;
    processingTime: {
        min: number;
        max: number;
        unit: DeliveryProcessingTimeUnit;
        preset?: string;
    };
    default: {
        type: DeliveryRateType;
        rate?: number;
        additionalItemRate?: number;
    };
    otherInternational?: {
        type: DeliveryRateType;
        rate?: number;
        additionalItemRate?: number;
    };
    zones: Array<{
        id: string;
        countries: Array<OptionItem>;
        type: DeliveryRateType;
        rate?: number;
        additionalItemRate?: number;
    }>;
}

export const processingTimePresets: Array<ProcessingTimePreset> = [
    { label: '1-2 business days', value: '1-2', min: 1, max: 2 },
    { label: '2-3 business days', value: '2-3', min: 2, max: 3 },
    { label: '3-5 business days', value: '3-5', min: 3, max: 5 },
    { label: '5-7 business days', value: '5-7', min: 5, max: 7 },
    { label: '7-14 business days', value: '7-14', min: 7, max: 14 },
    { label: 'Custom range', value: 'custom' }
];

export const defaultForm: DeliveryProfileForm = {
    name: '',
    saveProfile: false,
    originCountryCode: '',
    processingTime: {
        min: 0,
        max: 0,
        unit: DeliveryProcessingTimeUnit.Day,
        preset: ''
    },
    default: {
        type: DeliveryRateType.FreeDelivery,
        rate: undefined,
        additionalItemRate: undefined
    },
    otherInternational: {
        type: DeliveryRateType.FreeDelivery,
        rate: undefined,
        additionalItemRate: undefined
    },
    zones: []
};

/**
 * Maps a fetched DeliveryProfile onto the Formik form shape. Presets only
 * apply to day-based processing times; anything else edits as a custom range.
 */
export const profileToFormValues = (profile: DeliveryProfile): DeliveryProfileForm => {
    const matchingPreset = profile.processingTime.unit === DeliveryProcessingTimeUnit.Day
        ? processingTimePresets.find(preset => {
            if (preset.value === 'custom') return false;
            return preset.min === profile.processingTime.min && preset.max === profile.processingTime.max;
        })
        : undefined;

    return {
        name: profile.name || '',
        saveProfile: false, // Don't check the save profile box when editing
        originCountryCode: profile.originCountryCode,
        originRegionCode: profile.originRegionCode || undefined,
        originPostalCode: profile.originPostalCode || undefined,
        processingTime: {
            min: profile.processingTime.min,
            max: profile.processingTime.max,
            unit: profile.processingTime.unit,
            preset: matchingPreset?.value || 'custom'
        },
        default: {
            type: profile.default.type,
            rate: profile.default.rate || undefined,
            additionalItemRate: profile.default.additionalItemRate || undefined
        },
        otherInternational: profile.otherInternational ? {
            type: profile.otherInternational.type,
            rate: profile.otherInternational.rate || undefined,
            additionalItemRate: profile.otherInternational.additionalItemRate || undefined
        } : undefined,
        zones: profile.zones.map(zone => ({
            id: zone.id,
            countries: zone.countries.map(country => ({
                label: getCountryName(country.code),
                value: country.code
            })),
            type: zone.rate.type,
            rate: zone.rate.rate || undefined,
            additionalItemRate: zone.rate.additionalItemRate || undefined
        }))
    };
};

/**
 * Maps submitted form values to the SetDeliveryProfileInput the mutation
 * sends. The preset field is UI-only and never leaves the form; every zone
 * shares the profile-level processing time.
 */
export const formValuesToProfileInput = (
    values: DeliveryProfileForm,
    deliveryProfileId?: string
): SetDeliveryProfileInput => {
    const processingTime = {
        min: values.processingTime.min,
        max: values.processingTime.max,
        unit: values.processingTime.unit
    };

    return {
        ...(deliveryProfileId ? { id: deliveryProfileId } : {}),
        name: values.name,
        originCountryCode: values.originCountryCode,
        originRegionCode: values.originRegionCode,
        originPostalCode: values.originPostalCode,
        processingTime,
        default: {
            type: values.default.type,
            rate: values.default.rate,
            additionalItemRate: values.default.additionalItemRate
        },
        otherInternational: values.otherInternational ? {
            type: values.otherInternational.type,
            rate: values.otherInternational.rate,
            additionalItemRate: values.otherInternational.additionalItemRate
        } : undefined,
        zones: values.zones.map(zone => ({
            id: zone.id,
            countries: zone.countries.map(country => ({
                countryCode: country.value
            })),
            processingTime,
            rate: {
                type: zone.type,
                rate: zone.rate,
                additionalItemRate: zone.additionalItemRate
            }
        }))
    };
};
